/**
 * AURA Slider Control
 */

export interface SliderOptions {
  id: string;
  label: string;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  showValue?: boolean;
  onChange?: (value: number) => void;
}

const SLIDER_STYLES = `
  .aura-slider {
    display: flex;
    flex-direction: column;
    gap: var(--aura-spacing-xs);
    padding: var(--aura-spacing-sm) 0;
  }

  .aura-slider__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .aura-slider__label {
    font-size: var(--aura-font-size-md);
    color: var(--aura-text);
  }

  .aura-slider__value {
    font-size: var(--aura-font-size-sm);
    color: var(--aura-text-muted);
    font-weight: 500;
  }

  .aura-slider__controls {
    display: flex;
    align-items: center;
    gap: var(--aura-spacing-sm);
  }

  .aura-slider__btn {
    width: 32px;
    height: 32px;
    border-radius: var(--aura-radius-full);
    background: var(--aura-bg-secondary);
    color: var(--aura-text);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: bold;
    transition: background-color var(--aura-transition-fast);
  }

  .aura-slider__btn:hover {
    background: var(--aura-bg-hover);
  }

  .aura-slider__btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .aura-slider__input {
    flex: 1;
    height: 6px;
    border-radius: var(--aura-radius-full);
    background: var(--aura-border);
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
  }

  .aura-slider__input::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: var(--aura-radius-full);
    background: var(--aura-primary);
    cursor: pointer;
    box-shadow: var(--aura-shadow-sm);
  }

  .aura-slider__input::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: var(--aura-radius-full);
    background: var(--aura-primary);
    cursor: pointer;
    border: none;
    box-shadow: var(--aura-shadow-sm);
  }

  .aura-slider__input:focus-visible {
    outline: 2px solid var(--aura-border-focus);
    outline-offset: 2px;
  }
`;

export function createSlider(options: SliderOptions): { element: HTMLElement; styles: string; setValue: (v: number) => void } {
  const min = options.min ?? 0;
  const max = options.max ?? 100;
  const step = options.step ?? 1;
  let value = options.value ?? min;

  const container = document.createElement('div');
  container.className = 'aura-slider';

  const header = document.createElement('div');
  header.className = 'aura-slider__header';

  const label = document.createElement('label');
  label.className = 'aura-slider__label';
  label.setAttribute('for', options.id);
  label.textContent = options.label;

  const valueDisplay = document.createElement('span');
  valueDisplay.className = 'aura-slider__value';
  valueDisplay.textContent = `${value}${options.unit || ''}`;

  header.appendChild(label);
  if (options.showValue !== false) {
    header.appendChild(valueDisplay);
  }

  const controls = document.createElement('div');
  controls.className = 'aura-slider__controls';

  const decreaseBtn = document.createElement('button');
  decreaseBtn.className = 'aura-slider__btn';
  decreaseBtn.type = 'button';
  decreaseBtn.textContent = '−';
  decreaseBtn.setAttribute('aria-label', `Decrease ${options.label}`);

  const input = document.createElement('input');
  input.type = 'range';
  input.id = options.id;
  input.className = 'aura-slider__input';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  input.setAttribute('aria-valuenow', String(value));
  input.setAttribute('aria-valuemin', String(min));
  input.setAttribute('aria-valuemax', String(max));

  const increaseBtn = document.createElement('button');
  increaseBtn.className = 'aura-slider__btn';
  increaseBtn.type = 'button';
  increaseBtn.textContent = '+';
  increaseBtn.setAttribute('aria-label', `Increase ${options.label}`);

  controls.appendChild(decreaseBtn);
  controls.appendChild(input);
  controls.appendChild(increaseBtn);

  container.appendChild(header);
  container.appendChild(controls);

  const updateDisplay = (newValue: number) => {
    value = Math.max(min, Math.min(max, newValue));
    input.value = String(value);
    valueDisplay.textContent = `${value}${options.unit || ''}`;
    input.setAttribute('aria-valuenow', String(value));
    decreaseBtn.disabled = value <= min;
    increaseBtn.disabled = value >= max;
  };

  const handleChange = (newValue: number) => {
    updateDisplay(newValue);
    options.onChange?.(value);
  };

  input.addEventListener('input', () => handleChange(parseFloat(input.value)));
  decreaseBtn.addEventListener('click', () => handleChange(value - step));
  increaseBtn.addEventListener('click', () => handleChange(value + step));

  // Initial state
  decreaseBtn.disabled = value <= min;
  increaseBtn.disabled = value >= max;

  return {
    element: container,
    styles: SLIDER_STYLES,
    setValue: updateDisplay,
  };
}

export { SLIDER_STYLES };
